import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMaterialDetail } from "@/api/hooks/useCennik";
import { materialPrimaryId, menoKovania } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Okno výberu z cenníka (KOLO 201) — jeden rám pre Vybrať hranu,
 * Vymeniť hranu, Upraviť materiál aj import: nadpis s popisom, obsah
 * (hľadanie, filtre, tabuľka — `MaterialSelect vOkne`), riadok akcií
 * okna a pätička „Vybrané: … | Zrušiť | Vybrať".
 *
 * Okno má **pevnú výšku**. Radix ho centruje na výšku, takže okno, ktoré
 * rastie s výsledkami, by posunulo zoznam pod kurzorom — druhé kliknutie
 * dvojkliku by padlo na susedný riadok (KOLO 169). Pás „Vybrané" je
 * preto dole v pätičke a miesto preň je tam od začiatku.
 */
export function VyberDialog({
  open,
  onOpenChange,
  nadpis,
  popis,
  vybraneId,
  vybraneDoplnok,
  potvrdText = "Vybrať",
  onPotvrd,
  potvrdZakazane = false,
  potvrdBezVyberu = false,
  akcie,
  vPate,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nadpis: ReactNode;
  /** Povinný — čo sa vyberá a kam to pôjde (aj pre čítačku obrazovky). */
  popis: ReactNode;
  /** Označená položka — pätička ju ukáže kódom a názvom. */
  vybraneId: string | null;
  /** Druhý riadok pätičky, napr. dôvod zhody. */
  vybraneDoplnok?: ReactNode;
  potvrdText?: ReactNode;
  onPotvrd: () => void;
  potvrdZakazane?: boolean;
  /** Potvrdiť sa dá aj bez označenej položky (import: Nenahrávať). */
  potvrdBezVyberu?: boolean;
  /** Akcie okna nad pätičkou (zapamätať, Nenahrávať, Pridať aj k hranám…). */
  akcie?: ReactNode;
  /** Tlačidlá v pätičke pred Zrušiť (Odobrať materiál). */
  vPate?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // `overflow-hidden` prepíše `overflow-y-auto` zo základného okna
          // (KOLO 212) — tu sa posúva len zoznam, okno má pevnú výšku
          "flex h-[min(88vh,780px)] w-[96vw] max-w-4xl flex-col gap-3 overflow-hidden p-4 sm:p-6",
          className,
        )}
        // Radix inak sfokusuje samotné okno a `autoFocus` na hľadaní by
        // neplatilo. Kurzor patrí do hľadania — to je to, čo user robí.
        onOpenAutoFocus={(e) => {
          const obsah = e.currentTarget as HTMLElement;
          const hladanie = obsah.querySelector<HTMLInputElement>(
            'input[type="text"], input[type="search"], input:not([type])',
          );
          if (hladanie) {
            e.preventDefault();
            hladanie.focus();
          }
        }}
      >
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="line-clamp-2" title={typeof nadpis === "string" ? nadpis : undefined}>
            {nadpis}
          </DialogTitle>
          <DialogDescription>{popis}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-2">{children}</div>

        {akcie ? <div className="flex flex-wrap items-center gap-2">{akcie}</div> : null}

        <div className="flex flex-wrap items-center gap-2 border-t pt-3 sm:flex-nowrap">
          <VybranePas id={vybraneId} doplnok={vybraneDoplnok} />
          {vPate}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Zrušiť
          </Button>
          <Button type="button" onClick={onPotvrd} disabled={potvrdZakazane || (!vybraneId && !potvrdBezVyberu)}>
            {potvrdText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * „Vybrané: KÓD — názov" s odkazom do eshopu. Položku doťahuje detailom
 * podľa id — nájde aj tú, ktorú zoznam práve skrýva filtrom.
 */
function VybranePas({ id, doplnok }: { id: string | null; doplnok?: ReactNode }): JSX.Element {
  const detail = useMaterialDetail(id ?? undefined);
  const m = id && detail.data?.data?.id === id ? detail.data.data : null;
  const kod = m ? materialPrimaryId(m) : null;
  return (
    // Na mobile pás na celý riadok, tlačidlá pod ním — `flex-1` samo
    // (základ 0) by ho natlačilo k tlačidlám a ostalo by z neho „V"
    <div className="flex min-h-[2.5rem] min-w-0 basis-full flex-col justify-center gap-0.5 sm:basis-0 sm:flex-1">
      {id ? (
        <>
          <span className="flex min-w-0 items-center gap-2 text-sm">
            <span className="truncate">
              <span className="font-semibold">Vybrané: </span>
              {m ? (
                <>
                  {kod ? <span className="font-mono text-[13px]">{kod}</span> : null}
                  {kod ? " — " : ""}
                  {menoKovania(m).hlavne}
                </>
              ) : (
                "Načítava sa…"
              )}
            </span>
            {/* Odkaz skladá server (`odkaz_na_material`) — `KOD_PRE_ESHOP`
                sa do TS neprepisuje (lekcia z KOLA 77) */}
            {m?.eshop_odkaz ? (
              <a
                href={m.eshop_odkaz}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 whitespace-nowrap text-xs underline underline-offset-2"
                title="Otvoriť položku v eshope dodávateľa"
              >
                Eshop ↗
              </a>
            ) : null}
          </span>
          {doplnok ? (
            <span className="truncate text-xs text-muted-foreground">{doplnok}</span>
          ) : null}
        </>
      ) : (
        <span className="text-sm text-muted-foreground">
          {doplnok ?? "Klikni na riadok — dvojklik alebo Enter ho rovno potvrdí."}
        </span>
      )}
    </div>
  );
}
